import { Button, Grid, IconButton } from '@mui/material';
import A from 'components/Link/Link';
import Modal from 'components/Modal/Modal';
import config, { Country } from 'config';
import { YAGA_EXTERNALS } from 'config/fe-routes';
import BuyerProtectionCashIcon from 'public/icons/buyer-protection-icons/cash.svg';
import BuyerProtectionLockIcon from 'public/icons/buyer-protection-icons/lock.svg';
import BuyerProtectionPaymentIcon from 'public/icons/buyer-protection-icons/payment.svg';
import BuyerProtectionSupportIcon from 'public/icons/buyer-protection-icons/support.svg';
import ShieldIcon from 'public/icons/icn-shield.svg';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import IosClose from 'react-ionicons/lib/IosClose';
import IosInformationCircleOutline from 'react-ionicons/lib/IosInformationCircleOutline';

import { usePriceFormatter } from '../../hooks/usePriceFormatter';

type BuyerProtectionModalProps = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  tooltipButtonClassName?: string;
  buyerSideFeesPercentage: number;
  buyerSideFeesFixed?: number;
};

type ContentBullet = {
  icon: ReactElement;
  title: string;
  text: string;
};

const contentBullets: { [key: string]: ContentBullet } = {
  SCAM_PREVENTION: {
    icon: <BuyerProtectionLockIcon />,
    title: 'components.buyer-protection-modal.explanation.bullets.scam-prevention-title',
    text: 'components.buyer-protection-modal.explanation.bullets.scam-prevention-text',
  },
  RETURN_POLICY: {
    icon: <BuyerProtectionCashIcon />,
    title: 'components.buyer-protection-modal.explanation.bullets.return-and-refund-policy-title',
    text: 'components.buyer-protection-modal.explanation.bullets.return-and-refund-policy-text',
  },
  SUPPORT: {
    icon: <BuyerProtectionSupportIcon />,
    title: 'components.buyer-protection-modal.explanation.bullets.customer-support-title',
    text: 'components.buyer-protection-modal.explanation.bullets.customer-support-text',
  },
  SECURE_PAYMENTS: {
    icon: <BuyerProtectionPaymentIcon />,
    title: 'components.buyer-protection-modal.explanation.bullets.secure-payments-title',
    text: 'components.buyer-protection-modal.explanation.bullets.secure-payments-text',
  },
};

const getBullets = ({ country }: { country: Country }): ContentBullet[] => {
  if (country === Country.EE) {
    return [contentBullets.SECURE_PAYMENTS, contentBullets.RETURN_POLICY, contentBullets.SUPPORT];
  }

  return [contentBullets.SCAM_PREVENTION, contentBullets.RETURN_POLICY, contentBullets.SUPPORT, contentBullets.SECURE_PAYMENTS];
};

export const BuyerProtectionModal = ({
  isOpen,
  setOpen,
  tooltipButtonClassName,
  buyerSideFeesPercentage,
  buyerSideFeesFixed,
}: BuyerProtectionModalProps) => {
  const { t } = useTranslation();
  const country = config.get('country');
  const formatPrice = usePriceFormatter();

  const content = {
    title: t('components.buyer-protection-modal.explanation.title'),
    bullets: getBullets({ country }).map((bullet) => ({
      ...bullet,
      title: t(bullet.title),
      text: t(bullet.text),
    })),
  };

  return (
    <>
      <IconButton className={tooltipButtonClassName} onClick={() => setOpen(true)}>
        <IosInformationCircleOutline />
      </IconButton>

      <Modal open={isOpen} onClose={() => setOpen(false)} closeIconVisible={false} className="buyer-protection-modal">
        <Grid container>
          <div className="buyer-protection-modal-header">
            <IconButton onClick={() => setOpen(false)} className="buyer-protection-confirmation-modal-close close-icon" edge="end">
              <IosClose />
            </IconButton>

            <ShieldIcon className="shield-icon" />

            <h2 className="buyer-protection-modal-title">{t('components.buyer-protection-modal.title')}</h2>

            {!!buyerSideFeesPercentage && (
              <h5 className="buyer-protection-modal-subtitle">
                {t('components.buyer-protection-modal.subtitle', {
                  percentage: buyerSideFeesPercentage,
                  fixedPrice: buyerSideFeesFixed ? `+ ${formatPrice(buyerSideFeesFixed, { minAmountOfCents: 2 })}` : '',
                })}
              </h5>
            )}
          </div>

          <div className="buyer-protection-modal-content">
            <div className="buyer-protection-confirmation-modal-content-title" dangerouslySetInnerHTML={{ __html: content.title }} />

            <div className="buyer-protection-confirmation-modal-content-bullets">
              {content.bullets.map((bullet) => (
                <div className="bullet" key={bullet.title}>
                  <div className="bullet-icon">{bullet.icon}</div>
                  <div className="bullet-content">
                    <div className="bullet-title">{bullet.title}</div>
                    <div className="bullet-text">{bullet.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <A className="read-more-link primary mt-3" href={YAGA_EXTERNALS.FAQ.BUYER_PROTECTION_FEE().href} target="_blank">
              {t('components.buyer-protection-modal.read-more')}
            </A>
          </div>
        </Grid>

        {/* Actions */}
        <div className="buyer-protection-modal-actions">
          {/* cancel */}
          <Button variant="contained" color="primary" onClick={() => setOpen(false)}>
            {t('components.buyer-protection-modal.ok')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
